import { useState } from 'react';
import { KeyValueEditor, pairsToRecord, recordToPairs } from './KeyValueEditor';

export type MarketOverrides = Record<string, Record<string, unknown>>;

/** Form-shape that preserves row order between renders. */
export interface MarketOverrideRow {
  market: string;
  pairs: Array<[string, string]>;
}

interface Props {
  rows: MarketOverrideRow[];
  onChange: (rows: MarketOverrideRow[]) => void;
}

/**
 * Edit `perMarketOverrides`: one collapsible row per market, each containing
 * a `KeyValueEditor` for that market's overrides. Adding markets is a
 * single button at the top; values are kept as strings (the renderer only
 * substitutes them into URL/guide templates so the runtime type does not
 * matter).
 */
export function MarketOverridesEditor({ rows, onChange }: Props) {
  const [openMarkets, setOpenMarkets] = useState<Set<string>>(() => new Set());

  const toggle = (market: string) => {
    setOpenMarkets((prev) => {
      const next = new Set(prev);
      if (next.has(market)) next.delete(market);
      else next.add(market);
      return next;
    });
  };

  const setMarket = (index: number, market: string) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, market } : r)));
  };
  const setPairs = (index: number, pairs: Array<[string, string]>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, pairs } : r)));
  };
  const removeMarket = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };
  const addMarket = () => {
    const next: MarketOverrideRow = { market: '', pairs: [['', '']] };
    onChange([...rows, next]);
    // Auto-expand the new row so the user sees where to type.
    setOpenMarkets((prev) => new Set(prev).add(`__new-${rows.length}`));
  };

  return (
    <div className="flex-col" style={{ gap: 8 }}>
      {rows.length === 0 && (
        <p className="muted small" style={{ margin: 0 }}>
          No per-market overrides yet. Add one for each TEXEC market the recipe
          should customise (e.g. token, currency, language).
        </p>
      )}
      {rows.map((row, i) => {
        const id = row.market || `__new-${i}`;
        const open = openMarkets.has(id);
        return (
          <div className="card" key={i} style={{ padding: 10 }}>
            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-ghost small"
                onClick={() => toggle(id)}
                title={open ? 'Collapse' : 'Expand'}
              >
                {open ? 'v' : '>'}
              </button>
              <input
                value={row.market}
                placeholder="Market (e.g. DK, MGA)"
                onChange={(e) => setMarket(i, e.target.value.toUpperCase())}
                style={{ width: 180 }}
              />
              <span className="muted small">
                {Object.keys(pairsToRecord(row.pairs)).length} key(s)
              </span>
              <span className="spacer" />
              <button
                type="button"
                className="btn small btn-danger"
                onClick={() => removeMarket(i)}
              >
                Remove market
              </button>
            </div>
            {open && (
              <div style={{ marginTop: 10 }}>
                <KeyValueEditor
                  pairs={row.pairs}
                  onChange={(pairs) => setPairs(i, pairs)}
                  keyPlaceholder="Override key (e.g. token)"
                  valuePlaceholder="Value"
                  hint="Common keys: token, currency, lang, mode, brand. Values become {{key}} placeholders."
                />
              </div>
            )}
          </div>
        );
      })}
      <div className="row">
        <button type="button" className="btn small" onClick={addMarket}>
          Add market
        </button>
      </div>
    </div>
  );
}

/** Form-state -> record (drop blank markets, drop blank keys). */
export function rowsToOverrides(rows: MarketOverrideRow[]): MarketOverrides {
  const out: MarketOverrides = {};
  for (const r of rows) {
    const market = r.market.trim().toUpperCase();
    if (!market) continue;
    out[market] = pairsToRecord(r.pairs);
  }
  return out;
}

/** Reverse of `rowsToOverrides` for initialising form state. */
export function overridesToRows(overrides: MarketOverrides | undefined): MarketOverrideRow[] {
  if (!overrides) return [];
  return Object.entries(overrides).map(([market, raw]) => {
    const stringified: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw ?? {})) {
      stringified[k] = v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v);
    }
    return { market, pairs: recordToPairs(stringified) };
  });
}
